/*
 * mpc-registry.js
 * MPC Gift Registry -- client behavior.
 *
 * Drives three surfaces:
 *   1. Hub page (page.my-registries)  -- list cards, create modal, share copy,
 *      more-menu actions, localStorage wishlist migration banner.
 *   2. Public page (page.registry)    -- token resolution, product hydration,
 *      reserve flow.
 *   3. Site-wide                       -- heart-button list-picker intercept
 *      for logged-in customers with registries; auto-adds for single-list
 *      customers, shows picker for customers with 2+ lists.
 *
 * The App Proxy backend (/apps/registry/*) is built separately. Until it is
 * configured, network calls fail gracefully: they console.warn and surface a
 * friendly toast so pages still render.
 */
(function () {
  'use strict';

  var PROXY = 'https://artists.mainepottery.com/apps/registry';
  var SHARE_BASE = 'https://mainepottery.com/pages/registry';
  var WL_KEY = 'theme4:wishlist:id';

  // Customer ID read from Liquid-rendered data attribute on the hub element.
  // Sent as X-Registry-Customer-Id header on all owner (authenticated) requests.
  var _customerId = '';

  /* ---------------------------------------------------------------- utils */

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $all(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function warnNoProxy(context) {
    console.warn('App Proxy not yet configured (' + context + ')');
  }

  var toastTimer = null;
  function toast(message) {
    var el = $('[data-mpc-reg-toast]');
    if (!el) { return; }
    el.textContent = message;
    el.hidden = false;
    el.classList.add('is-visible');
    if (toastTimer) { clearTimeout(toastTimer); }
    toastTimer = setTimeout(function () {
      el.classList.remove('is-visible');
      setTimeout(function () { el.hidden = true; }, 250);
    }, 2600);
  }

  function copyToClipboard(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text);
    }
    return new Promise(function (resolve, reject) {
      try {
        var ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        resolve();
      } catch (err) {
        reject(err);
      }
    });
  }

  function ownerHeaders(extra) {
    var h = Object.assign({ 'Content-Type': 'application/json', 'Accept': 'application/json' }, extra || {});
    if (_customerId) h['X-Registry-Customer-Id'] = _customerId;
    return h;
  }

  function postProxy(path, payload) {
    return fetch(PROXY + path, {
      method: 'POST',
      headers: ownerHeaders(),
      body: JSON.stringify(payload || {})
    }).then(function (res) {
      if (!res.ok) { throw new Error('Proxy responded ' + res.status); }
      return res.json();
    });
  }

  function getCurrentScript(page) {
    return $('script[data-mpc-registry-page="' + page + '"]');
  }

  /* --------------------------------------------------------- modal helpers */

  function openModal(modal) {
    if (!modal) { return; }
    modal.hidden = false;
    document.documentElement.classList.add('mpc-reg-modal-open');
    var firstField = modal.querySelector('input, select, button');
    if (firstField) { firstField.focus(); }
  }

  function closeModal(modal) {
    if (!modal) { return; }
    modal.hidden = true;
    document.documentElement.classList.remove('mpc-reg-modal-open');
  }

  /* ------------------------------------------------------------ HUB page */

  function initHub() {
    var hub = $('[data-mpc-registry-hub]');
    if (!hub) { return; }
    _customerId = hub.dataset.customerId || '';

    var modal = $('[data-mpc-reg-modal]', hub);
    var form = $('[data-mpc-reg-form]', hub);
    var formError = form ? $('[data-mpc-reg-form-error]', form) : null;

    function showFormError(msg) {
      if (!formError) { return; }
      formError.textContent = msg;
      formError.hidden = false;
    }

    /* Create modal open / close */
    $all('[data-mpc-reg-create-open]', hub).forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (form) {
          form.reset();
          var idField = form.querySelector('input[name="registry_id"]');
          if (idField) { idField.value = ''; }
        }
        var title = $('[data-mpc-reg-modal-title]', modal) || $('#mpc-reg-modal-title', modal);
        if (title) { title.textContent = 'Create a new list'; }
        if (formError) { formError.hidden = true; }
        openModal(modal);
      });
    });

    $all('[data-mpc-reg-modal-close]', hub).forEach(function (el) {
      el.addEventListener('click', function () { closeModal(modal); });
    });

    /* Create / rename submit */
    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (formError) { formError.hidden = true; }

        var data = new FormData(form);
        var payload = {
          registry_id: data.get('registry_id') || '',
          title: (data.get('title') || '').toString().trim(),
          occasion: data.get('occasion') || '',
          visibility: data.get('visibility') || 'link'
        };

        if (!payload.title) {
          showFormError('Please give your list a name.');
          return;
        }

        var submitBtn = $('[data-mpc-reg-form-submit]', form);
        if (submitBtn) { submitBtn.disabled = true; }

        var isUpdate = !!payload.registry_id;
        var url = isUpdate
          ? PROXY + '/lists/' + encodeURIComponent(payload.registry_id)
          : PROXY + '/lists';
        var body = { title: payload.title, occasion: payload.occasion };
        fetch(url, {
          method: isUpdate ? 'PUT' : 'POST',
          headers: ownerHeaders(),
          body: JSON.stringify(body)
        }).then(function (res) {
          if (!res.ok) { throw new Error('Proxy responded ' + res.status); }
          return res.json();
        }).then(function () {
          toast(isUpdate ? 'List updated!' : 'List created!');
          window.location.reload();
        }).catch(function () {
          warnNoProxy('create/update list');
          if (submitBtn) { submitBtn.disabled = false; }
          showFormError('Registry service is not connected yet. Please try again later.');
        });
      });
    }

    /* Share buttons -- copy link */
    $all('[data-mpc-reg-share]', hub).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var url = btn.getAttribute('data-mpc-reg-share-url');
        if (!url) { return; }
        copyToClipboard(url).then(function () {
          toast('Link copied!');
        }).catch(function () {
          toast('Could not copy -- ' + url);
        });
      });
    });

    /* More-menu dropdowns */
    $all('[data-mpc-reg-menu]', hub).forEach(function (menu) {
      var toggle = $('[data-mpc-reg-menu-toggle]', menu);
      var dropdown = $('.mpc-reg-card__dropdown', menu);
      if (!toggle || !dropdown) { return; }

      toggle.addEventListener('click', function (e) {
        e.stopPropagation();
        var isOpen = !dropdown.hidden;
        $all('.mpc-reg-card__dropdown', hub).forEach(function (d) { d.hidden = true; });
        $all('[data-mpc-reg-menu-toggle]', hub).forEach(function (t) {
          t.setAttribute('aria-expanded', 'false');
        });
        if (!isOpen) {
          dropdown.hidden = false;
          toggle.setAttribute('aria-expanded', 'true');
        }
      });
    });

    document.addEventListener('click', function () {
      $all('.mpc-reg-card__dropdown', hub).forEach(function (d) { d.hidden = true; });
      $all('[data-mpc-reg-menu-toggle]', hub).forEach(function (t) {
        t.setAttribute('aria-expanded', 'false');
      });
    });

    /* More-menu actions */
    $all('[data-mpc-reg-action]', hub).forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var action = btn.getAttribute('data-mpc-reg-action');
        var id = btn.getAttribute('data-mpc-reg-id');
        if (action === 'rename') {
          handleRename(id);
        } else if (action === 'set-default') {
          handleSetDefault(id);
        } else if (action === 'delete') {
          handleDelete(id, btn);
        }
      });
    });

    function handleRename(id) {
      var card = hub.querySelector('[data-mpc-reg-id="' + id + '"].mpc-reg-card')
        || hub.querySelector('.mpc-reg-card[data-mpc-reg-id="' + id + '"]');
      var current = card ? card.getAttribute('data-mpc-reg-title') : '';
      var next = window.prompt('Rename this list', current || '');
      if (next === null) { return; }
      next = next.trim();
      if (!next) { return; }
      fetch(PROXY + '/lists/' + encodeURIComponent(id), {
        method: 'PUT',
        headers: ownerHeaders(),
        body: JSON.stringify({ title: next })
      }).then(function (res) {
        if (!res.ok) { throw new Error('Proxy responded ' + res.status); }
        return res.json();
      }).then(function () {
        toast('List renamed!');
        window.location.reload();
      }).catch(function () {
        warnNoProxy('rename list');
        toast('Registry service is not connected yet.');
      });
    }

    function handleSetDefault(id) {
      fetch(PROXY + '/lists/' + encodeURIComponent(id), {
        method: 'PUT',
        headers: ownerHeaders(),
        body: JSON.stringify({ set_default: true })
      }).then(function (res) {
        if (!res.ok) { throw new Error('Proxy responded ' + res.status); }
        return res.json();
      }).then(function () {
        toast('Default list updated!');
        window.location.reload();
      }).catch(function () {
        warnNoProxy('set default list');
        toast('Registry service is not connected yet.');
      });
    }

    function handleDelete(id, btn) {
      var card = btn.closest('.mpc-reg-card');
      var title = card ? card.getAttribute('data-mpc-reg-title') : 'this list';
      if (!window.confirm('Delete "' + title + '"? This cannot be undone.')) { return; }
      fetch(PROXY + '/lists/' + encodeURIComponent(id), {
        method: 'DELETE',
        headers: ownerHeaders({ 'Accept': 'application/json' })
      }).then(function (res) {
        if (!res.ok) { throw new Error('Proxy responded ' + res.status); }
        return res.json();
      }).then(function () {
        toast('List deleted.');
        window.location.reload();
      }).catch(function () {
        warnNoProxy('delete list');
        toast('Registry service is not connected yet.');
      });
    }

    /* Escape closes modal */
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal && !modal.hidden) { closeModal(modal); }
    });
  }

  /* ------------------------------------------ localStorage wishlist migrate */

  function readLegacyWishlist() {
    var raw = null;
    try { raw = window.localStorage.getItem(WL_KEY); } catch (err) { return []; }
    if (!raw) { return []; }
    var ids = [];
    try {
      var parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        parsed.forEach(function (entry) {
          if (typeof entry === 'number' || typeof entry === 'string') {
            ids.push(String(entry));
          } else if (entry && (entry.id || entry.variant_id)) {
            ids.push(String(entry.id || entry.variant_id));
          }
        });
      }
    } catch (err) {
      ids = raw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    }
    return ids.filter(Boolean);
  }

  /* --------------------------------------------------------- PUBLIC page */

  function initPublic() {
    var root = $('[data-mpc-registry-public]');
    if (!root) { return; }

    var script = getCurrentScript('public');
    var shopUrl = (script && script.getAttribute('data-mpc-shop-url')) || '';

    var loadingEl = $('[data-mpc-reg-loading]', root);
    var errorEl = $('[data-mpc-reg-error]', root);
    var contentEl = $('[data-mpc-reg-content]', root);
    var emptyEl = $('[data-mpc-reg-empty]', root);
    var itemsEl = $('[data-mpc-reg-items]', root);
    var tmpl = $('[data-mpc-reg-item-template]', root);

    function show(el) { if (el) { el.hidden = false; } }
    function hide(el) { if (el) { el.hidden = true; } }

    function showError() {
      hide(loadingEl);
      hide(contentEl);
      show(errorEl);
    }

    var params = new URLSearchParams(window.location.search);
    var token = params.get('token');
    if (!token) { showError(); return; }

    fetch(PROXY + '/resolve?token=' + encodeURIComponent(token), {
      headers: { 'Accept': 'application/json' }
    }).then(function (res) {
      if (!res.ok) { throw new Error('Proxy responded ' + res.status); }
      return res.json();
    }).then(function (data) {
      window.__mpc_registry_data = data;
      renderRegistry(data);
    }).catch(function () {
      warnNoProxy('resolve token');
      showError();
    });

    function renderRegistry(data) {
      hide(loadingEl);

      if (!data) { showError(); return; }

      var occEl = $('[data-mpc-reg-occasion]', root);
      var titleEl = $('[data-mpc-reg-list-title]', root);
      var ownerEl = $('[data-mpc-reg-owner]', root);

      if (occEl) { occEl.textContent = data.occasion || 'Gift Registry'; }
      if (titleEl) { titleEl.textContent = data.title || 'A Maine Pottery registry'; }
      if (ownerEl && data.owner_name) { ownerEl.textContent = 'Created by ' + data.owner_name; }

      show(contentEl);

      var items = Array.isArray(data.items) ? data.items : [];
      if (!items.length) {
        show(emptyEl);
        return;
      }

      items.forEach(function (item) {
        var card = buildItemCard(item);
        if (card) { itemsEl.appendChild(card); }
      });

      hydrateProducts(items);
    }

    function buildItemCard(item) {
      if (!tmpl || !('content' in tmpl)) { return null; }
      var node = tmpl.content.firstElementChild.cloneNode(true);
      node.setAttribute('data-mpc-reg-variant', item.variant_id || '');
      node.setAttribute('data-mpc-reg-product', item.product_id || '');

      var noteEl = $('[data-mpc-reg-item-note]', node);
      if (noteEl && item.note) {
        noteEl.textContent = item.note;
        noteEl.hidden = false;
      }

      if (item.reserved) { markClaimed(node); }

      var claimBtn = $('[data-mpc-reg-item-claim]', node);
      if (claimBtn) {
        claimBtn.addEventListener('click', function () {
          openReserveModal(item, node);
        });
      }

      return node;
    }

    function markClaimed(node) {
      node.classList.add('is-claimed');
      var overlay = $('[data-mpc-reg-item-claimed]', node);
      if (overlay) { overlay.hidden = false; }
      var claimBtn = $('[data-mpc-reg-item-claim]', node);
      if (claimBtn) {
        claimBtn.disabled = true;
        claimBtn.textContent = 'Claimed';
      }
    }

    function hydrateProducts(items) {
      var handles = [];
      items.forEach(function (item) {
        if (item.product_handle) { handles.push(item.product_handle); }
      });

      items.forEach(function (item) {
        if (!item.product_handle) { return; }
        var node = itemsEl.querySelector(
          '[data-mpc-reg-variant="' + (item.variant_id || '') + '"]'
        );
        if (!node) { return; }
        fetch('/products/' + item.product_handle + '.js', {
          headers: { 'Accept': 'application/json' }
        }).then(function (res) {
          if (!res.ok) { throw new Error('product ' + res.status); }
          return res.json();
        }).then(function (product) {
          fillProduct(node, product, item);
        }).catch(function () {
          fillProductFallback(node, item);
        });
      });
    }

    function fillProduct(node, product, item) {
      var url = (shopUrl || '') + '/products/' + product.handle;
      var variant = null;
      if (item.variant_id && product.variants) {
        variant = product.variants.filter(function (v) {
          return String(v.id) === String(item.variant_id);
        })[0] || null;
      }

      var img = $('[data-mpc-reg-item-img]', node);
      if (img) {
        var src = (variant && variant.featured_image && variant.featured_image.src)
          || product.featured_image || '';
        if (src) {
          img.src = src;
          img.alt = product.title || '';
        }
      }

      var vendorEl = $('[data-mpc-reg-item-vendor]', node);
      if (vendorEl) { vendorEl.textContent = product.vendor || ''; }

      var titleLink = $('[data-mpc-reg-item-title-link]', node);
      if (titleLink) {
        titleLink.textContent = product.title || '';
        titleLink.href = url;
      }

      $all('[data-mpc-reg-item-link], [data-mpc-reg-item-buy]', node).forEach(function (a) {
        a.href = url;
      });

      var priceEl = $('[data-mpc-reg-item-price]', node);
      if (priceEl) {
        var cents = variant ? variant.price : product.price;
        priceEl.textContent = formatMoney(cents);
      }
    }

    function fillProductFallback(node, item) {
      var titleLink = $('[data-mpc-reg-item-title-link]', node);
      if (titleLink) {
        titleLink.textContent = item.title || 'View product';
        if (item.product_handle) {
          titleLink.href = (shopUrl || '') + '/products/' + item.product_handle;
        }
      }
    }

    function formatMoney(cents) {
      if (cents === null || cents === undefined) { return ''; }
      var dollars = (Number(cents) / 100);
      return '$' + dollars.toFixed(2);
    }

    /* Reserve modal */
    var reserveModal = $('[data-mpc-reg-reserve-modal]', root);
    var reserveForm = reserveModal ? $('[data-mpc-reg-reserve-form]', reserveModal) : null;
    var reserveError = reserveModal ? $('[data-mpc-reg-reserve-error]', reserveModal) : null;
    var reserveItemLabel = reserveModal ? $('[data-mpc-reg-reserve-item]', reserveModal) : null;
    var activeItem = null;
    var activeNode = null;

    function openReserveModal(item, node) {
      activeItem = item;
      activeNode = node;
      if (reserveForm) { reserveForm.reset(); }
      if (reserveError) { reserveError.hidden = true; }
      if (reserveItemLabel) {
        var titleLink = $('[data-mpc-reg-item-title-link]', node);
        reserveItemLabel.textContent = titleLink ? titleLink.textContent : '';
      }
      openModal(reserveModal);
    }

    $all('[data-mpc-reg-reserve-close]', root).forEach(function (el) {
      el.addEventListener('click', function () { closeModal(reserveModal); });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && reserveModal && !reserveModal.hidden) {
        closeModal(reserveModal);
      }
    });

    if (reserveForm) {
      reserveForm.addEventListener('submit', function (e) {
        e.preventDefault();
        if (!activeItem) { return; }
        if (reserveError) { reserveError.hidden = true; }

        var name = (new FormData(reserveForm)).get('purchaser_name') || '';
        postProxy('/reserve', {
          token: token,
          variant_id: activeItem.variant_id,
          purchaser_name: name.toString().trim()
        }).then(function () {
          if (activeNode) { markClaimed(activeNode); }
          closeModal(reserveModal);
          toast('Thank you! We marked it as yours.');
        }).catch(function () {
          warnNoProxy('reserve item');
          if (reserveError) {
            reserveError.textContent = 'Registry service is not connected yet. Please try again later.';
            reserveError.hidden = false;
          }
        });
      });
    }
  }

  /* ---------------------------------------- site-wide heart list-picker */

  function initListPicker() {
    var hearts = $all('hdt-wishlist[data-mpc-registry="true"]');
    if (!hearts.length) { return; }

    hearts.forEach(function (heart) {
      heart.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        showListPicker(heart);
      }, true);
    });
  }

  function showListPicker(heart) {
    closeListPicker();

    var pop = document.createElement('div');
    pop.className = 'mpc-reg-picker';
    pop.setAttribute('data-mpc-reg-picker', '');
    pop.innerHTML =
      '<p class="mpc-reg-picker__title">Add to which list?</p>' +
      '<div class="mpc-reg-picker__loading">Loading your lists&hellip;</div>';

    document.body.appendChild(pop);
    positionPicker(pop, heart);

    fetch(PROXY + '/lists', { headers: ownerHeaders({ 'Accept': 'application/json' }) })
      .then(function (res) {
        if (!res.ok) { throw new Error('Proxy responded ' + res.status); }
        return res.json();
      })
      .then(function (data) {
        renderPickerLists(pop, heart, (data && data.lists) || []);
      })
      .catch(function () {
        warnNoProxy('fetch lists for picker');
        var loading = pop.querySelector('.mpc-reg-picker__loading');
        if (loading) {
          loading.textContent = 'Could not load your lists right now.';
        }
        setTimeout(closeListPicker, 1600);
      });
  }

  function renderPickerLists(pop, heart, lists) {
    var loading = pop.querySelector('.mpc-reg-picker__loading');
    if (loading) { loading.remove(); }

    // Clear any previously rendered body so re-renders stay clean.
    $all('.mpc-reg-picker__list, .mpc-reg-picker__empty, .mpc-reg-picker__create, .mpc-reg-picker__newbtn', pop)
      .forEach(function (el) { el.remove(); });

    if (!lists.length) {
      var empty = document.createElement('p');
      empty.className = 'mpc-reg-picker__empty';
      empty.textContent = 'Start your first list to save this.';
      pop.appendChild(empty);
    } else {
      var ul = document.createElement('ul');
      ul.className = 'mpc-reg-picker__list';
      lists.forEach(function (list) {
        var li = document.createElement('li');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'mpc-reg-picker__item';
        btn.textContent = list.title || 'Untitled list';
        btn.addEventListener('click', function () {
          addToListThenWishlist(heart, list.id, list.title || '');
          closeListPicker();
        });
        li.appendChild(btn);
        ul.appendChild(li);
      });
      pop.appendChild(ul);
    }

    // Always offer "+ New list" -- the primary CTA when there are no lists yet.
    var newBtn = document.createElement('button');
    newBtn.type = 'button';
    newBtn.className = 'mpc-reg-picker__newbtn'
      + (lists.length ? '' : ' mpc-reg-picker__newbtn--primary');
    newBtn.textContent = '+ New list';
    newBtn.addEventListener('click', function () {
      showCreateForm(pop, heart);
    });
    pop.appendChild(newBtn);
  }

  // Inline "create a list" form inside the picker popup. Mirrors the hub's
  // create modal (title required, optional occasion). On success the new list
  // immediately receives the current item, then the picker closes.
  function showCreateForm(pop, heart) {
    $all('.mpc-reg-picker__list, .mpc-reg-picker__empty, .mpc-reg-picker__newbtn', pop)
      .forEach(function (el) { el.remove(); });

    var title = pop.querySelector('.mpc-reg-picker__title');
    if (title) { title.textContent = 'Name your list'; }

    var form = document.createElement('form');
    form.className = 'mpc-reg-picker__create';
    form.innerHTML =
      '<input class="mpc-reg-picker__input" type="text" name="title" required ' +
      'maxlength="80" placeholder="Things I love" autocomplete="off">' +
      '<select class="mpc-reg-picker__input" name="occasion">' +
      '<option value="">No occasion</option>' +
      '<option value="Wedding">Wedding</option>' +
      '<option value="Birthday">Birthday</option>' +
      '<option value="New Home">New Home</option>' +
      '<option value="Housewarming">Housewarming</option>' +
      '<option value="Holiday">Holiday</option>' +
      '<option value="Other">Other</option>' +
      '</select>' +
      '<div class="mpc-reg-picker__error" data-err hidden></div>' +
      '<div class="mpc-reg-picker__create-actions">' +
      '<button type="button" class="mpc-reg-picker__cancel" data-cancel>Cancel</button>' +
      '<button type="submit" class="mpc-reg-picker__save">Create &amp; add</button>' +
      '</div>';
    pop.appendChild(form);

    var input = form.querySelector('input[name="title"]');
    if (input) { input.focus(); }

    form.querySelector('[data-cancel]').addEventListener('click', function () {
      closeListPicker();
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var err = form.querySelector('[data-err]');
      if (err) { err.hidden = true; }

      var data = new FormData(form);
      var listTitle = (data.get('title') || '').toString().trim();
      var occasion = (data.get('occasion') || '').toString().trim();
      if (!listTitle) {
        if (err) { err.textContent = 'Please name your list.'; err.hidden = false; }
        return;
      }

      var saveBtn = form.querySelector('.mpc-reg-picker__save');
      if (saveBtn) { saveBtn.disabled = true; }

      fetch(PROXY + '/lists', {
        method: 'POST',
        headers: ownerHeaders(),
        body: JSON.stringify({ title: listTitle, occasion: occasion })
      }).then(function (res) {
        if (!res.ok) { throw new Error('Proxy responded ' + res.status); }
        return res.json();
      }).then(function (resp) {
        var list = (resp && resp.list) || {};
        return addToListThenWishlist(heart, list.id, list.title || listTitle);
      }).then(function () {
        closeListPicker();
      }).catch(function () {
        warnNoProxy('create list from picker');
        if (saveBtn) { saveBtn.disabled = false; }
        if (err) {
          err.textContent = 'Could not create your list right now.';
          err.hidden = false;
        }
      });
    });
  }

  // POST the heart's product to a registry list. Reads the real variant id,
  // product id and handle from the heart's data attributes (injected by the
  // card render sites). Registry-only: no localStorage re-fire of the heart.
  function addToListThenWishlist(anchor, listId, listTitle) {
    var variantId = anchor.getAttribute('data-variant-id')
      || anchor.getAttribute('data-id') || '';
    var productId = anchor.getAttribute('data-product-id')
      || anchor.getAttribute('data-id') || '';
    var productHandle = anchor.getAttribute('data-handle') || '';

    return postProxy('/lists/' + encodeURIComponent(listId) + '/items', {
      variant_id: variantId,
      product_id: productId,
      product_handle: productHandle
    })
      .then(function () {
        toast(listTitle ? 'Added to ' + listTitle + '!' : 'Added to your list!');
        markHeartAdded(anchor);
      })
      .catch(function () {
        warnNoProxy('add item to list');
        toast('Could not add to your list right now.');
      });
  }

  // Reflect a successful registry add in the UI. On the wishlist page the
  // "Add to Registry" button turns into a confirmed/disabled state; everywhere
  // else the heart itself takes the "added" active state.
  function markHeartAdded(anchor) {
    if (anchor.getAttribute('data-mpc-wl-context') === 'wishlist') {
      markCardAddedToRegistry(anchor);
      return;
    }
    anchor.classList.add('is-added');
    anchor.setAttribute('aria-pressed', 'true');
  }

  // Turn the wishlist "Add to Registry" button into a confirmed/disabled state.
  function markCardAddedToRegistry(btn) {
    btn.disabled = true;
    btn.classList.add('mpc-wl-item__add--done');
    btn.innerHTML =
      '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" ' +
      'stroke="currentColor" stroke-width="2.4" stroke-linecap="round" ' +
      'stroke-linejoin="round" aria-hidden="true">' +
      '<polyline points="20 6 9 17 4 12"></polyline></svg> In registry';
  }

  function positionPicker(pop, anchor) {
    var rect = anchor.getBoundingClientRect();
    var top = window.scrollY + rect.bottom + 8;
    var left = window.scrollX + rect.left;
    var maxLeft = window.scrollX + document.documentElement.clientWidth - 240;
    if (left > maxLeft) { left = maxLeft; }
    if (left < 8) { left = 8; }
    pop.style.top = top + 'px';
    pop.style.left = left + 'px';
  }

  function closeListPicker() {
    var existing = $('[data-mpc-reg-picker]');
    if (existing) { existing.remove(); }
  }

  document.addEventListener('click', function (e) {
    var pop = $('[data-mpc-reg-picker]');
    if (!pop) { return; }
    if (pop.contains(e.target)) { return; }
    if (e.target.closest && e.target.closest('hdt-wishlist[data-mpc-registry="true"]')) { return; }
    if (e.target.closest && e.target.closest('[data-mpc-wl-add]')) { return; }
    closeListPicker();
  });

  /* ------------------------------------------------------------ WISHLIST */
  /*
   * First-class wishlist page (sections/main-wishlist.liquid).
   *
   * Owner view: variant IDs come from localStorage (WL_KEY). Shared read-only
   * view: variant IDs come from ?ids=VID1,VID2 in the URL. Each variant ID is
   * resolved to a product handle via the per-variant URL /variants/<id>, which
   * 302-redirects to /products/<handle>?variant=<id> (the storefront search
   * id: field matches the PRODUCT id, not the variant id, so it can't be used
   * here). The product is then hydrated from /products/<handle>.js and the card
   * bound to the matching variant. The <img> is only ever given a src when a
   * real image exists -- otherwise the no-photo zone stays visible (never a
   * broken image).
   */

  var WL_SHARE_BASE = 'https://mainepottery.com/pages/wishlist';

  function wlFormatMoney(cents) {
    if (cents === null || cents === undefined || cents === '') { return ''; }
    return '$' + (Number(cents) / 100).toFixed(2);
  }

  // Parse variant IDs out of ?ids=... (read-only shared view).
  function wlReadSharedIds() {
    var raw = new URLSearchParams(window.location.search).get('ids');
    if (!raw) { return null; }
    var ids = raw.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    return ids.length ? ids : [];
  }

  // Resolve variant IDs to product handles. Returns a Promise of a map
  // { variantId: handle } (variant IDs that could not be resolved are absent).
  //
  // The stock storefront search `id:` field matches the PRODUCT id, not the
  // variant id, so it can't resolve a wishlist's variant IDs. Instead we hit
  // the per-variant URL `/variants/<id>`, which 302-redirects to
  // `/products/<handle>?variant=<id>`. fetch() follows the redirect, so the
  // resolved product handle is read from res.url. One request per variant, but
  // exact -- no fuzzy search, no false matches.
  function wlResolveVariantHandle(variantId) {
    return fetch('/variants/' + encodeURIComponent(variantId), {
      headers: { 'Accept': 'text/html' },
      redirect: 'follow'
    })
      .then(function (res) {
        if (!res.ok) { return null; }
        var m = (res.url || '').match(/\/products\/([^/?#]+)/);
        return m ? m[1] : null;
      })
      .catch(function () { return null; });
  }

  function wlResolveHandles(variantIds) {
    var map = {};
    if (!variantIds.length) { return Promise.resolve(map); }

    return Promise.all(variantIds.map(function (variantId) {
      return wlResolveVariantHandle(variantId).then(function (handle) {
        if (handle) { map[String(variantId)] = handle; }
      });
    })).then(function () { return map; });
  }

  function initWishlist() {
    var root = $('[data-mpc-wishlist]');
    if (!root) { return; }

    var grid = $('[data-mpc-wl-grid]', root);
    var emptyEl = $('[data-mpc-wl-empty]', root);
    var countEl = $('[data-mpc-wl-count]', root);
    var shareBtn = $('[data-mpc-wl-share]', root);
    var shareWrap = $('[data-mpc-wl-share-wrap]', root);
    var itemTmpl = $('[data-mpc-wl-item-template]', root);
    var skelTmpl = $('[data-mpc-wl-skeleton-template]', root);
    if (!grid || !itemTmpl || !('content' in itemTmpl)) { return; }

    var script = getCurrentScript('wishlist');
    var shopUrl = (script && script.getAttribute('data-mpc-shop-url')) || '';

    var ownerId = root.getAttribute('data-mpc-wl-owner') || '';
    _customerId = ownerId; // enables ownerHeaders() for Add-to-Registry

    var sharedIds = wlReadSharedIds();
    var isReadOnly = !!sharedIds;          // a shared link, viewed by anyone
    var hasRegistries = script && script.getAttribute('data-mpc-has-registries') === 'true';
    var hasCustomer = script && script.getAttribute('data-mpc-customer') === 'true';

    // Legacy /pages/wishlist is retired. Only the shared, read-only ?ids= view
    // still renders here. Logged-in owners go to the registry hub; guests get a
    // sign-in prompt. (A Liquid inline redirect in main-wishlist.liquid also
    // handles the logged-in case before JS runs; this is the belt-and-braces.)
    if (!isReadOnly) {
      if (hasCustomer) {
        window.location.replace('/pages/my-registries');
        return;
      }
      showSignInPrompt();
      return;
    }

    // Variant IDs to render (shared read-only view).
    var variantIds = sharedIds;

    applyReadOnlyChrome();

    if (!variantIds.length) {
      showEmpty();
      return;
    }

    function showSignInPrompt() {
      var signin = $('[data-mpc-wl-signin]', root);
      if (signin) { signin.removeAttribute('hidden'); }
      if (grid) { grid.innerHTML = ''; }
      if (emptyEl) { emptyEl.setAttribute('hidden', ''); }
      if (countEl) { countEl.setAttribute('hidden', ''); }
      if (shareBtn) { shareBtn.setAttribute('hidden', ''); }
      if (shareWrap) { shareWrap.classList.remove('is-open'); }
    }

    // Render skeletons immediately so the grid shows the right shape.
    renderSkeletons(variantIds.length);

    var resolved = 0;   // products that hydrated successfully
    var failed = 0;     // variant IDs that 404'd / could not resolve
    var total = variantIds.length;

    wlResolveHandles(variantIds).then(function (map) {
      // map is { variantId: handle }. Collect the unique handles to fetch; a
      // single product can satisfy multiple requested variant IDs.
      var pending = variantIds.slice();
      var handles = [];
      variantIds.forEach(function (id) {
        var handle = map[String(id)];
        if (handle && handles.indexOf(handle) === -1) { handles.push(handle); }
      });

      if (!handles.length) {
        // Nothing resolved -- everything is gone.
        variantIds.forEach(function () { onCardFailed(); });
        return;
      }

      Promise.all(handles.map(function (handle) {
        return fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
          .then(function (res) {
            if (!res.ok) { throw new Error('product ' + res.status); }
            return res.json();
          })
          .then(function (product) {
            (product.variants || []).forEach(function (variant) {
              var idx = pending.indexOf(String(variant.id));
              if (idx === -1) { return; }
              pending.splice(idx, 1);
              renderCard(product, variant);
            });
          })
          .catch(function () { /* handled by the pending sweep below */ });
      })).then(function () {
        // Any variant IDs still pending never resolved -> treat as gone.
        pending.forEach(function () { onCardFailed(); });
      });
    });

    /* ---------------------------------------------------------- render */

    function renderSkeletons(n) {
      grid.innerHTML = '';
      if (!skelTmpl || !('content' in skelTmpl)) { return; }
      for (var i = 0; i < n; i++) {
        grid.appendChild(skelTmpl.content.firstElementChild.cloneNode(true));
      }
    }

    // Pop the first skeleton (left-to-right fill) and return the slot, or null.
    function takeSkeletonSlot() {
      return grid.querySelector('.mpc-wl-item--skeleton');
    }

    function renderCard(product, variant) {
      var node = itemTmpl.content.firstElementChild.cloneNode(true);
      node.setAttribute('data-mpc-wl-variant', variant.id);

      var url = (shopUrl || '') + '/products/' + product.handle
        + (variant.id ? '?variant=' + variant.id : '');

      // Image -- variant image preferred, product featured image fallback,
      // otherwise leave the no-photo zone visible. Never a broken <img>.
      var media = $('.mpc-wl-item__media', node);
      var img = $('[data-mpc-wl-img]', node);
      var src = (variant.featured_image && variant.featured_image.src)
        || product.featured_image || '';
      if (img && src) {
        img.src = src;
        img.alt = product.title || '';
        img.removeAttribute('hidden');
        if (media) { media.classList.add('has-image'); }
      }

      var vendorEl = $('[data-mpc-wl-vendor]', node);
      if (vendorEl) { vendorEl.textContent = product.vendor || ''; }

      var titleLink = $('[data-mpc-wl-title-link]', node);
      if (titleLink) {
        titleLink.textContent = product.title || 'View product';
        titleLink.href = url;
      }

      var priceEl = $('[data-mpc-wl-price]', node);
      if (priceEl) {
        priceEl.textContent = wlFormatMoney(
          variant.price !== undefined ? variant.price : product.price
        );
      }

      $all('[data-mpc-wl-link], [data-mpc-wl-view]', node).forEach(function (a) {
        a.href = url;
      });

      // Remove button -- owner only.
      var removeBtn = $('[data-mpc-wl-remove]', node);
      if (removeBtn) {
        if (isReadOnly) {
          removeBtn.remove();
        } else {
          removeBtn.addEventListener('click', function () {
            removeFromWishlist(variant.id, node);
          });
        }
      }

      // Add-to-Registry -- owner with at least one registry only.
      var addBtn = $('[data-mpc-wl-add]', node);
      var createLink = $('[data-mpc-wl-create-registry]', node);
      if (addBtn) {
        if (!isReadOnly && hasRegistries) {
          addBtn.setAttribute('data-variant-id', variant.id);
          addBtn.removeAttribute('hidden');
          addBtn.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            showListPicker(addBtn);
          });
        } else if (!isReadOnly && createLink) {
          // Logged in but no registries yet -- offer to make one.
          createLink.removeAttribute('hidden');
        }
      }

      placeCard(node);
      onCardResolved();
    }

    // Replace the next skeleton with the real card (left-to-right fill).
    function placeCard(node) {
      var slot = takeSkeletonSlot();
      if (slot) {
        grid.replaceChild(node, slot);
      } else {
        grid.appendChild(node);
      }
    }

    function onCardResolved() {
      resolved++;
      updateCount();
    }

    function onCardFailed() {
      failed++;
      // Drop one trailing skeleton so the grid shrinks to the real count.
      var slots = $all('.mpc-wl-item--skeleton', grid);
      if (slots.length) { slots[slots.length - 1].remove(); }
      finalize();
    }

    function finalize() {
      // If every fetch failed (e.g. all items deleted from Shopify), show empty.
      if (failed >= total && resolved === 0) {
        showEmpty();
      } else {
        updateCount();
      }
    }

    /* ---------------------------------------------------------- count */

    function liveCount() {
      return $all('[data-mpc-wl-item]', grid).length;
    }

    function updateCount() {
      var n = liveCount();
      if (!countEl) { return; }
      if (n > 0) {
        countEl.textContent = n + (n === 1 ? ' item' : ' items');
        countEl.removeAttribute('hidden');
      } else {
        countEl.setAttribute('hidden', '');
      }
    }

    /* --------------------------------------------------------- remove */

    function removeFromWishlist(variantId, node) {
      // Update localStorage.
      try {
        var ids = readLegacyWishlist().filter(function (id) {
          return String(id) !== String(variantId);
        });
        window.localStorage.setItem(WL_KEY, JSON.stringify(ids.map(Number)));
      } catch (err) { /* non-fatal */ }

      // Animate the card out, then remove it.
      node.classList.add('is-removing');
      var done = function () {
        if (node.parentNode) { node.parentNode.removeChild(node); }
        updateCount();
        if (liveCount() === 0) { showEmpty(); }
        updateShareUrl();
      };
      var motionOk = !window.matchMedia
        || !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (motionOk) {
        var t = setTimeout(done, 320);
        node.addEventListener('transitionend', function () {
          clearTimeout(t); done();
        }, { once: true });
      } else {
        done();
      }
      toast('Removed from wishlist');
    }

    /* ---------------------------------------------------------- empty */

    function showEmpty() {
      grid.innerHTML = '';
      if (emptyEl) { emptyEl.removeAttribute('hidden'); }
      if (countEl) { countEl.setAttribute('hidden', ''); }
      if (shareBtn) { shareBtn.setAttribute('hidden', ''); }
      if (shareWrap) { shareWrap.classList.remove('is-open'); }
    }

    /* --------------------------------------------------- read-only UI */

    function applyReadOnlyChrome() {
      root.classList.add('is-readonly');
      var eyebrow = $('[data-mpc-wl-eyebrow]', root);
      var title = $('[data-mpc-wl-title]', root);
      if (eyebrow) { eyebrow.textContent = 'Shared Wishlist'; }
      if (title) { title.textContent = 'Things they love'; }
      if (shareBtn) { shareBtn.setAttribute('hidden', ''); }
    }

    /* ---------------------------------------------------------- share */

    function currentShareUrl() {
      var liveIds = $all('[data-mpc-wl-item]', grid).map(function (li) {
        return li.getAttribute('data-mpc-wl-variant');
      }).filter(Boolean);
      var ids = liveIds.length ? liveIds : variantIds;
      return WL_SHARE_BASE + '?ids=' + ids.join(',');
    }

    function updateShareUrl() {
      var url = currentShareUrl();
      var input = $('[data-mpc-wl-share-url]', root);
      if (input) { input.value = url; }

      var enc = encodeURIComponent(url);
      var pin = $('[data-mpc-wl-share-pinterest]', root);
      var fb = $('[data-mpc-wl-share-facebook]', root);
      var em = $('[data-mpc-wl-share-email]', root);
      if (pin) {
        pin.href = 'https://pinterest.com/pin/create/button/?url=' + enc
          + '&description=My+Maine+Pottery+wishlist';
      }
      if (fb) {
        fb.href = 'https://www.facebook.com/sharer/sharer.php?u=' + enc;
      }
      if (em) {
        em.href = 'mailto:?subject=My+Maine+Pottery+wishlist&body=' + enc;
      }
    }

    if (!isReadOnly && shareBtn && shareWrap) {
      shareBtn.removeAttribute('hidden');
      updateShareUrl();

      shareBtn.addEventListener('click', function () {
        var open = shareWrap.classList.toggle('is-open');
        shareBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
        shareWrap.setAttribute('aria-hidden', open ? 'false' : 'true');
        if (open) {
          updateShareUrl();
          var input = $('[data-mpc-wl-share-url]', root);
          if (input) { input.focus(); input.select(); }
        }
      });

      var copyBtn = $('[data-mpc-wl-copy]', root);
      if (copyBtn) {
        copyBtn.addEventListener('click', function () {
          copyToClipboard(currentShareUrl())
            .then(function () { toast('Link copied!'); })
            .catch(function () { toast('Could not copy the link.'); });
        });
      }
    }
  }

  /* -------------------------------------------- product page (PDP) heart */
  /*
   * The Kalles product buy-button block (blocks/_product-buy-button.liquid)
   * already renders a registry heart next to add-to-cart, and initListPicker()
   * already wires it to open the list-picker. The only gap on a product page is
   * the SELECTED VARIANT: the block injects the first/initial variant at render
   * time, but a shopper can switch variants before saving. So here we keep each
   * product-form heart's data-variant-id in sync with the live product-form
   * [name="id"] selection (on change, and again at click time). Card hearts on
   * the page (related/recently) are untouched -- they live outside the buy form
   * and already carry their own per-card variant.
   */
  function initPdpHeart() {
    var script = getCurrentScript('site');
    var pageType = script ? script.getAttribute('data-mpc-page-type') : '';
    var isProduct = pageType === 'product'
      || /(^|\s)hdt-page-type-product(\s|$)/.test(document.body.className || '');
    if (!isProduct) { return; }

    $all('hdt-wishlist[data-mpc-registry="true"]').forEach(function (heart) {
      var form = heart.closest('form');
      if (!form) { return; }
      var input = form.querySelector('[name="id"]');
      if (!input) { return; } // only enhance hearts inside the product buy form

      function syncVariant() {
        if (input.value) { heart.setAttribute('data-variant-id', String(input.value)); }
      }
      syncVariant();                                  // seed from current selection
      form.addEventListener('change', syncVariant);   // track variant switches
      heart.addEventListener('click', syncVariant, true); // belt-and-braces at click
    });
  }

  /* ------------------------------------------------------------- boot */

  // Site-wide bundle (loaded from layout/theme.liquid for logged-in customers).
  // Seeds the customer id so the heart list-picker can authenticate on any page.
  function initSite() {
    var script = getCurrentScript('site');
    if (!script) { return; }
    _customerId = script.getAttribute('data-mpc-customer-id') || _customerId;
  }

  function boot() {
    initSite();
    initHub();
    initPublic();
    initListPicker();
    initPdpHeart();
    initWishlist();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();

